import React, { Component } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';
import { Grid, Option } from '@inplayer-org/inplayer-ui';

// utils
import includes from 'lodash/includes';
import values from 'lodash/values';
import { transformSearchParams, isDevelopmentEnv } from 'utils/helpers';

// Actions
import {
  loadFollowers as loadFollowersAction,
  createFollowerAccount as createFollowerAccountAction,
  updateFollowerAccount as updateFollowerAccountAction,
  loadPaymentMethods as loadPaymentMethodsAction,
  deleteFollowerAccount as deleteFollowerAccountAction,
  loadFollowerPaymentMethods as loadFollowerPaymentMethodsAction,
  QueryParams,
  UpdateFollowerPayload,
} from 'ducks/merchantDucks/agency/actions';
import {
  impersonateAsAgent,
  fetchAdminMerchantDetails,
  resetAdminMerchantDetailsState,
  fetchPaymentProviders as fetchPaymentProvidersAction,
} from 'ducks/adminDucks/merchants/actions';
import { openModal as openModalAction } from 'ducks/merchantDucks/modal/actions';

// types
import AgencyState, { Follower } from 'ducks/merchantDucks/agency/types';
import RootState from 'ducks/RootState';
import PaymentMethodsState from 'ducks/merchantDucks/paymentMethods/types';
import { ModalNames } from 'ducks/merchantDucks/modal/types';
import { AdminMerchantAccount } from 'ducks/adminDucks/merchants/types';
import { User, Roles } from 'ducks/merchantDucks/user/types';
import { Scopes } from 'pages/AdminPages/AdminMerchants/PaymentManagement/components/PaymentSetup';

// Components
import ConfirmModal from 'components/Modals/ConfirmModal';
import ContentHolder from 'components/ContentHolder';
import SubheaderWithBorder from 'components/SubheaderWithBorder';
import MainHolder from 'components/MainHolder';
import Filter from 'components/Filter';
import { BlueTypography } from 'components/SharedStyledElements';
import SearchTooltip from 'components/SearchTooltip';
import BreadcrumbLink from 'components/BreadcrumbLink';
import ClientAccountsTable from 'pages/MerchantPages/Account/AgencySettings/components/tables/ClientAccountsTable';
import ClientModal from 'pages/MerchantPages/Account/AgencySettings/components/modals/ClientModal';
import ImpersonateModal from 'pages/MerchantPages/Account/AgencySettings/components/modals/ImpersonateModal';
import AttentionMessage from 'components/AttentionMessage';

const { Container, Cell } = Grid;

interface SearchParams {
  AccountID: number;
  Name: string;
  ConsumerEmail: string;
}

const options: Array<Option> = [
  { value: 'ConsumerEmail', displayName: 'Email' },
  { value: 'AccountID', displayName: 'Account ID' },
  { value: 'Name', displayName: 'Name' },
];

interface StateProps {
  followers: AgencyState;
  paymentMethods: PaymentMethodsState;
  user: User;
  adminUser: AdminMerchantAccount;
  isAdmin: boolean;
  masterMethods: Array<any>;
}

interface DispatchProps {
  loadFollowers: (params?: QueryParams) => any;
  createFollowerAccount: (follower: UpdateFollowerPayload) => any;
  updateFollowerAccount: (follower: UpdateFollowerPayload) => any;
  deleteFollowerAccount: (followerId: number) => any;
  loadPaymentMethods: () => any;
  loginAsFollower: (data: any) => any;
  loadFollowerPaymentMethods: ({ merchantID, all }: { merchantID: number; all: boolean }) => any;
  openModal: (modalName: ModalNames) => void;
  adminMerchantDetails: (merchandId: number | string) => any;
  resetAdminMerchantDetails: () => any;
  fetchPaymentProviders: (merchantId: number) => any;
}

interface MatchParams {
  id: string;
}

interface MatchProps extends RouteComponentProps<MatchParams> {}

interface OwnProps extends MatchProps {}

type Props = StateProps & DispatchProps & OwnProps;

interface State {
  page: number;
  tags: Array<string>;
  follower: Partial<Follower>;
  editMode: boolean;
  noMethods: boolean;
  followerId: number;
  masterID: string;
  addMerchantMode: boolean;
}

export class ClientAccounts extends Component<Props, State> {
  state: State = {
    page: 1,
    tags: [],
    follower: {},
    editMode: false,
    noMethods: false,
    followerId: -1,
    // eslint-disable-next-line react/destructuring-assignment
    masterID: this.props.match.params.id,
    addMerchantMode: false,
  };

  async componentDidMount() {
    const {
      match: {
        params: { id: merchantId },
      },
      user: { id: userId },
    } = this.props;

    const id = merchantId || userId;
    if (id) {
      const { adminMerchantDetails, fetchPaymentProviders } = this.props;
      await adminMerchantDetails(id);
      await fetchPaymentProviders(Number(id));
    }

    const {
      adminUser: { roles: adminRoles },
    } = this.props;

    const {
      loadPaymentMethods,
      user: { roles },
    } = this.props;

    let isAgancyEnabled = false;
    if (merchantId) {
      isAgancyEnabled = includes(adminRoles, Roles.MASTER);
    } else {
      isAgancyEnabled = includes(roles, Roles.MASTER);
    }
    this.redirectIfNotMaster(!isAgancyEnabled);
    this.loadFollowers();
    loadPaymentMethods();
  }

  redirectIfNotMaster = (isNotMaster: boolean) => {
    const { history, isAdmin } = this.props;
    if (isNotMaster) {
      history.replace(isAdmin ? '/admin/merchants' : '/dashboard');
    }
  };

  loadFollowers = () => {
    const { loadFollowers } = this.props;
    const { page, tags, masterID } = this.state;

    const params = {
      page: page - 1,
      size: 6,
      search: tags,
      order: 'createdAt',
      masterID,
    };

    loadFollowers(params);
  };

  handleAddTag = (searchParams: SearchParams) => {
    const transformedParams = transformSearchParams(searchParams);
    this.setState(
      () => ({
        tags: transformedParams,
        page: 1,
      }),
      this.loadFollowers
    );
  };

  handleDeleteTag = (i: number) => {
    const { tags: stateTags } = this.state;
    if (stateTags.length === 0) {
      return;
    }

    this.setState(
      ({ tags }) => ({
        tags: [...tags.slice(0, i), ...tags.slice(i + 1)],
        page: 1,
      }),
      this.loadFollowers
    );
  };

  handlePageClick = (pageNumber: number) => {
    this.setState({ page: pageNumber }, this.loadFollowers);
  };

  handleAddFollower = () => {
    const { openModal } = this.props;
    this.setState(
      {
        editMode: false,
        addMerchantMode: false,
      },
      () => openModal(ModalNames.Client)
    );
  };

  handleUpdateExistingMerchant = () => {
    const { openModal } = this.props;
    const { resetAdminMerchantDetails } = this.props;
    resetAdminMerchantDetails();
    this.setState(
      {
        addMerchantMode: true,
      },
      () => openModal(ModalNames.Client)
    );
  };

  allFalse = (methods?: { [key: number]: boolean }) => {
    if (values(methods).length === 0) {
      return false;
    }
    const result = values(methods).filter((value) => value);

    return result.length === 0;
  };

  handleCreateClient = async (clientData: UpdateFollowerPayload) => {
    const { createFollowerAccount, isAdmin } = this.props;

    const { scopes: selectedScopes, methods } = clientData;
    // Show error message for payment methods if inheritance scope is
    // selected and follower has no payment method chosen
    if (includes(selectedScopes, Scopes.INHERITANCE) && this.allFalse(methods)) {
      this.setState({ noMethods: true });
      return;
    }
    // Hide error message for payment methods
    this.setState({ noMethods: false });

    let newClientData = {
      ...clientData,
      scopes: selectedScopes,
    };

    if (isAdmin) {
      // eslint-disable-next-line react/destructuring-assignment
      newClientData = { ...newClientData, master_id: this.state.masterID };
    }
    // If inheritance scope is selected
    if (includes(selectedScopes, Scopes.INHERITANCE)) {
      newClientData = {
        ...newClientData,
        methods,
      };
    }

    await createFollowerAccount(newClientData);
  };

  handleUpdateClient = async (updateClientData: UpdateFollowerPayload) => {
    const { updateFollowerAccount } = this.props;

    const {
      follower: { id: followerId },
    } = this.state;

    const { scopes: selectedScopes, methods } = updateClientData;
    // Show error message for payment methods if inheritance scope is
    // selected and follower has no payment method chosen
    if (includes(selectedScopes, Scopes.INHERITANCE) && this.allFalse(methods)) {
      this.setState({ noMethods: true });
      return;
    }
    // Hide error message for payment methods
    this.setState({ noMethods: false });

    let clientData = {
      scopes: selectedScopes,
      follower_id: followerId,
      methods: {} as { [key: number]: boolean },
    };
    // If inheritance scope is selected
    if (includes(selectedScopes, Scopes.INHERITANCE)) {
      clientData = {
        ...clientData,
        methods: methods || {},
      };
    }

    await updateFollowerAccount(clientData);
  };

  handleManageInheritance = async (follower: Follower) => {
    const { loadFollowerPaymentMethods, openModal } = this.props;
    const { id } = follower;
    this.setState({
      editMode: true,
      follower,
      noMethods: false,
      addMerchantMode: false,
    });
    await loadFollowerPaymentMethods({ merchantID: id, all: true });
    openModal(ModalNames.Client);
  };

  openDeleteModal = (followerId: number) => {
    const { openModal } = this.props;
    this.setState({ followerId }, () => {
      openModal(ModalNames.RemoveRelation);
    });
  };

  handleRemoveFollower = async () => {
    const { deleteFollowerAccount } = this.props;
    const { followerId } = this.state;
    await deleteFollowerAccount(followerId);
  };

  openImpersonateModal = (follower: Follower) => {
    const { openModal } = this.props;
    this.setState({ follower }, () => {
      openModal(ModalNames.Impersonate);
    });
  };

  handleLoginAsFollower = async (data: any) => {
    const {
      follower: { id, email },
    } = this.state;
    let {
      user: { merchant_uuid: uuid },
    } = this.props;

    const { isAdmin, adminUser } = this.props;
    if (isAdmin) {
      const { merchant_uuid: merchantUuid } = adminUser;
      uuid = merchantUuid;
    }
    // eslint-disable-next-line no-param-reassign
    data = {
      ...data,
      sign_in_as: id,
      uuid,
      email,
      client_id: uuid,
      grant_type: 'social',
    };

    const { loginAsFollower } = this.props;
    try {
      await loginAsFollower(data);
    } catch (_) {}
  };

  renderTableHeaderSection = () => {
    return (
      <Container>
        <Cell width={12}>
          <SearchTooltip />
        </Cell>
        <Cell width={12}>
          <Filter options={options} onSearch={this.handleAddTag} />
        </Cell>
      </Container>
    );
  };

  render() {
    const {
      followers,
      paymentMethods: { followerMethods, isFetching },
      masterMethods,
    } = this.props;
    const { scopeOptions } = followers;
    const { page, follower, editMode, noMethods, masterID, addMerchantMode } = this.state;

    return (
      <>
        <MainHolder>
          <SubheaderWithBorder>
            <ContentHolder>
              <BlueTypography variant="h1">Related accounts</BlueTypography>
            </ContentHolder>
          </SubheaderWithBorder>
          <ContentHolder>
            {masterID && (
              <BreadcrumbLink fontSize="1.3" to={`/admin/merchants/edit/${masterID}`}>
                ← Back to master
              </BreadcrumbLink>
            )}
            {isDevelopmentEnv && !masterID && <AttentionMessage />}
            <Container>
              <Cell width={12}>
                <ClientAccountsTable
                  paginationPage={page}
                  followers={followers}
                  handlePageClick={this.handlePageClick}
                  handleAddClient={this.handleAddFollower}
                  handleUpdateExistingMerchant={this.handleUpdateExistingMerchant}
                  handleManageInheritance={this.handleManageInheritance}
                  openDeleteModal={this.openDeleteModal}
                  openImpersonateModal={this.openImpersonateModal}
                  renderHeaderSection={this.renderTableHeaderSection}
                />
              </Cell>
            </Container>
          </ContentHolder>
        </MainHolder>
        <ClientModal
          scopeOptions={scopeOptions}
          handleCreateUpdateClient={editMode ? this.handleUpdateClient : this.handleCreateClient}
          paymentMethods={masterMethods}
          followerMethods={followerMethods}
          isFetching={isFetching}
          follower={follower}
          editMode={editMode}
          noMethods={noMethods}
          addMerchantMode={addMerchantMode}
        />
        <ConfirmModal
          modalName={ModalNames.RemoveRelation}
          message="Are you sure you want to remove this related account?"
          handleConfirm={this.handleRemoveFollower}
          description="By removing a related account you will no longer be able to access it or manage it’s settings from the master account. Any inherited settings will also be removed. The related account will still remain active."
          title="Remove related account"
        />
        <ImpersonateModal follower={follower} handleLoginAsFollower={this.handleLoginAsFollower} />
      </>
    );
  }
}

export const mapStateToProps: MapStateToProps<StateProps, OwnProps, RootState> = (state) => {
  return {
    followers: state.agency,
    paymentMethods: state.paymentMethods,
    user: state.auth.user,
    adminUser: state.merchants.details.account,
    isAdmin: state.auth.isAdmin,
    masterMethods: state.merchants.paymentSetup.currentPaymentMethodsCollection,
  };
};

const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = {
  loadFollowers: loadFollowersAction,
  createFollowerAccount: createFollowerAccountAction,
  updateFollowerAccount: updateFollowerAccountAction,
  loadPaymentMethods: loadPaymentMethodsAction,
  deleteFollowerAccount: deleteFollowerAccountAction,
  loginAsFollower: impersonateAsAgent,
  loadFollowerPaymentMethods: loadFollowerPaymentMethodsAction,
  openModal: openModalAction,
  adminMerchantDetails: fetchAdminMerchantDetails,
  resetAdminMerchantDetails: resetAdminMerchantDetailsState,
  fetchPaymentProviders: fetchPaymentProvidersAction,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ClientAccounts));
